/**
 * LiveTestingBodySelectTreeField 컴포넌트
 *
 * body_select (트리 기반) 타입을 위한 Live Testing 필드
 * V2 전용 구현 - V1 훅 의존성 제거
 *
 * 주요 기능:
 * - "BODY PART" 버튼 클릭 시 V2 LiveTestingBodySelectTreeModal 모달 열기
 * - 선택된 body part 그룹을 리스트로 표시
 * - 편집/삭제 기능
 */

import { memo, useCallback, useMemo, useState } from 'react';
import { BiPencil, BiTrash } from 'react-icons/bi';
import { TbCirclePlus } from 'react-icons/tb';
import { isEmpty, truncate, upperCase } from 'lodash';
import produce from 'immer';
import type { QuestionResponseDto, BodySelectTreeConfigResponseDto } from '../../../../api/generated/types';
import type { LiveTestingAnswerValue } from './types';
import { useStaticLocales, type SystemLocaleCodes } from '../../../../../admin/hooks/locales/use-static-locale';
import { useTranslations } from '../../../../../patient/hooks/use-translations';
import LiveTestingBodySelectTreeModal from './LiveTestingBodySelectTreeModal';
import LiveTestingBodySelectField, { BodySelectValue } from './LiveTestingBodySelectField';
import LiveTestingContainer from './LiveTestingContainer';
import { normalizeQuestionLabelForSurveyLocale } from '../../../../shared/utils/survey-locale-text';
import { getLocaleCode } from '../../../organization/configurations/language/constants';
import {
  useQuestionControllerGetBodySelectTreeConfig,
  useQuestionControllerFindByTreeId,
} from '../../../../api/generated/questions/questions';
import {
  useQuestionTreeControllerFindByTreeKey,
} from '../../../../api/generated/question-trees/question-trees';

/**
 * body_select 트리 타입의 값 구조
 * selectedKeyGroups: 각 body part 선택 세션을 담는 배열
 * notesForKeyGroups: 각 그룹의 key별 description을 담는 배열
 */
export interface BodySelectTreeValue {
  selectedKeyGroups: string[][];
  notesForKeyGroups?: Array<Record<string, string>>;
  answerMaps?: Array<Record<string, LiveTestingAnswerValue>>;
}


interface LiveTestingBodySelectTreeFieldProps {
  question: QuestionResponseDto;
  value: BodySelectTreeValue;
  onChange: (value: BodySelectTreeValue) => void;
  disabled?: boolean;
}

function LiveTestingBodySelectTreeField({
  question,
  value,
  onChange,
  disabled = false,
}: LiveTestingBodySelectTreeFieldProps) {
  // LiveTestingContainer에서 currentLocale, surveyId, isMale 가져오기
  const {
    currentLocale,
    surveyId,
    bodySelectTreeConfigMap,
    bodySelectTreeConfigLoadingMap,
    bodySelectTreeFollowUpQuestionsMap,
    bodySelectTreeReadyMap,
    isMale,
  } = LiveTestingContainer.useContainer();

  const localeCode = getLocaleCode(currentLocale || 'english') as SystemLocaleCodes;
  const { sltStr } = useStaticLocales({ localeCode });
  const [, { t }] = useTranslations({ locale: currentLocale });

  // treeKey 추출
  const treeKey = question.answerKeys?.[0];
  const isInlineBodyOptionMode =
    !treeKey || treeKey.startsWith('o-');

  // Container에서 body_select tree config / follow-up 정보를 가져오기
  const configFromMap = treeKey ? bodySelectTreeConfigMap[treeKey] : undefined;
  const isLoadingFromMap = treeKey ? bodySelectTreeConfigLoadingMap[treeKey] : false;
  const followUpQuestionsFromContainer = treeKey
    ? bodySelectTreeFollowUpQuestionsMap[treeKey] || []
    : [];
  const isTreeReadyFromContainer = treeKey ? bodySelectTreeReadyMap[treeKey] || false : false;

  // Fallback: Container에 데이터가 없으면 (서브트리 body_select 등) 자체적으로 API 호출
  const needsSelfFetch = !!treeKey && !treeKey.startsWith('o-') && !configFromMap && !isLoadingFromMap;

  const selfConfigQuery = useQuestionControllerGetBodySelectTreeConfig(
    { surveyId: Number(surveyId || 0), locale: currentLocale || 'english', treeKey: treeKey || '' },
    { query: { enabled: needsSelfFetch && !!surveyId } },
  );

  const selfTreeInfoQuery = useQuestionTreeControllerFindByTreeKey(
    { surveyId: Number(surveyId || 0), treeKey: treeKey || '' },
    { query: { enabled: needsSelfFetch && !!surveyId } },
  );

  const selfTreeId = Number((selfTreeInfoQuery.data as { id?: number })?.id || 0);

  const selfTreeQuestionsQuery = useQuestionControllerFindByTreeId(
    selfTreeId || 0,
    { locale: currentLocale || 'english' },
    { query: { enabled: needsSelfFetch && selfTreeId > 0 } },
  );

  // Container 데이터 or self-fetch fallback 병합
  const treeConfig = configFromMap || (selfConfigQuery.data as BodySelectTreeConfigResponseDto | undefined);
  const isTreeConfigLoading = needsSelfFetch ? selfConfigQuery.isFetching : isLoadingFromMap;

  const selfFollowUpQuestions = useMemo(() => {
    if (!needsSelfFetch) return [];
    const entryQuestionKey = treeConfig?.entryQuestionKey || treeConfig?.entryQuestion?.questionKey;
    const treeQuestions = (selfTreeQuestionsQuery.data || []) as QuestionResponseDto[];
    return treeQuestions.filter((q) => {
      if (entryQuestionKey && q.questionKey === entryQuestionKey) return false;
      return q.enabled !== false;
    });
  }, [needsSelfFetch, treeConfig, selfTreeQuestionsQuery.data]);

  const followUpQuestionsFromMap = needsSelfFetch ? selfFollowUpQuestions : followUpQuestionsFromContainer;

  const selfIsTreeReady = useMemo(() => {
    if (!needsSelfFetch) return false;
    const treeInfo = selfTreeInfoQuery.data as { id?: number } | undefined;
    const treeQuestions = (selfTreeQuestionsQuery.data || []) as QuestionResponseDto[];
    const entryQuestionKey = treeConfig?.entryQuestionKey || treeConfig?.entryQuestion?.questionKey;
    const hasTreeQuestions = treeQuestions.length > 0;
    const hasEntryQuestion = !!entryQuestionKey && treeQuestions.some((q) => q.questionKey === entryQuestionKey);
    return Boolean(treeInfo) && hasTreeQuestions && hasEntryQuestion;
  }, [needsSelfFetch, selfTreeInfoQuery.data, selfTreeQuestionsQuery.data, treeConfig]);

  const isTreeReadyFromMap = needsSelfFetch ? selfIsTreeReady : isTreeReadyFromContainer;

  // 라벨 가져오기 (V1과 동일하게 useTranslations 사용)
  const getBodyPartLabel = useCallback((key: string) => {
    return t(key) || key;
  }, [t]);

  // 선택된 키 그룹들
  const selectedKeyGroups = value?.selectedKeyGroups || [];
  const notesForKeyGroups = value?.notesForKeyGroups || [];
  const answerMaps = value?.answerMaps || [];

  // 모달 상태: -1 = 새로 추가, >= 0 = 기존 항목 편집, null = 닫힘
  const [modalIdx, setModalIdx] = useState<number | null>(null);

  // 현재 편집 중인 항목의 선택된 키들
  const modalInitialKeys = useMemo(() => {
    if (modalIdx == null || modalIdx < 0) return [];
    return selectedKeyGroups[modalIdx] || [];
  }, [selectedKeyGroups, modalIdx]);

  // 현재 편집 중인 항목의 notesForKeys
  const modalInitialNotes = useMemo(() => {
    if (modalIdx == null || modalIdx < 0) return {};
    return notesForKeyGroups[modalIdx] || {};
  }, [notesForKeyGroups, modalIdx]);

  const modalInitialAnswers = useMemo(() => {
    if (modalIdx == null || modalIdx < 0) return {};
    return answerMaps[modalIdx] || {};
  }, [answerMaps, modalIdx]);

  // 모달 닫기
  const handleClose = useCallback(() => {
    setModalIdx(null);
  }, []);

  // 모달에서 저장
  const handleSave = useCallback(
    (
      selectedKeys: string[],
      notesForKeys: Record<string, string>,
      answers: Record<string, LiveTestingAnswerValue>
    ) => {
      if (selectedKeys.length === 0) {
        setModalIdx(null);
        return;
      }

      let newKeyGroups: string[][];
      let newNotesGroups: Array<Record<string, string>>;
      let newAnswerMaps: Array<Record<string, LiveTestingAnswerValue>>;
      if (modalIdx != null && modalIdx > -1) {
        // 기존 항목 수정
        newKeyGroups = produce(selectedKeyGroups, (dr) => {
          if (dr?.[modalIdx]) {
            dr[modalIdx] = selectedKeys;
          }
        });
        newNotesGroups = produce(notesForKeyGroups, (dr) => {
          dr[modalIdx] = notesForKeys;
        });
        newAnswerMaps = produce(answerMaps, (dr) => {
          dr[modalIdx] = answers;
        });
      } else {
        // 새 항목 추가
        newKeyGroups = [...selectedKeyGroups, selectedKeys];
        newNotesGroups = [...notesForKeyGroups, notesForKeys];
        newAnswerMaps = [...answerMaps, answers];
      }

      onChange({
        selectedKeyGroups: newKeyGroups,
        notesForKeyGroups: newNotesGroups,
        answerMaps: newAnswerMaps,
      });
      setModalIdx(null);
    },
    [selectedKeyGroups, notesForKeyGroups, answerMaps, modalIdx, onChange]
  );

  // 삭제
  const handleDelete = useCallback(
    (idx: number) => {
      const newKeyGroups = selectedKeyGroups.filter((_, idx2) => idx !== idx2);
      const newNotesGroups = notesForKeyGroups.filter((_, idx2) => idx !== idx2);
      const newAnswerMaps = answerMaps.filter((_, idx2) => idx !== idx2);
      onChange({
        selectedKeyGroups: newKeyGroups,
        notesForKeyGroups: newNotesGroups,
        answerMaps: newAnswerMaps,
      });
    },
    [selectedKeyGroups, notesForKeyGroups, answerMaps, onChange]
  );

  // 추가 버튼 클릭
  const handleAddClick = useCallback(() => {
    setModalIdx(-1);
  }, []);

  // 편집 버튼 클릭
  const handleEditClick = useCallback((idx: number) => {
    setModalIdx(idx);
  }, []);

  // 일부 body_select 질문은 트리 키가 아니라 option key(o-*)를 직접 담고 있음.
  // 이 경우 트리 모달 경로로 가면 비어 보일 수 있어, 인라인 body select로 fallback.
  if (isInlineBodyOptionMode) {
    const inlineValue: BodySelectValue = {
      answerKeys: selectedKeyGroups[0] || [],
      notesForKeys: notesForKeyGroups[0] || {},
    };
    return (
      <LiveTestingBodySelectField
        question={{
          ...question,
          inputType: 'body_select_whole',
        }}
        value={inlineValue}
        onChange={(next) =>
          onChange({
            selectedKeyGroups: [next.answerKeys || []],
            notesForKeyGroups: [next.notesForKeys || {}],
            answerMaps: answerMaps.length > 0 ? answerMaps : [{} as Record<string, LiveTestingAnswerValue>],
          })}
        disabled={disabled}
        isMale={isMale}
      />
    );
  }

  return (
    <>
      {/* V2 LiveTestingBodySelectTreeModal */}
      <LiveTestingBodySelectTreeModal
        isOpen={modalIdx != null}
        title={normalizeQuestionLabelForSurveyLocale({
          locale: currentLocale,
          label: question.label,
        })}
        initialSelectedKeys={modalInitialKeys}
        initialNotesForKeys={modalInitialNotes}
        initialAnswerMap={modalInitialAnswers}
        onSave={handleSave}
        onClose={handleClose}
        locale={currentLocale}
        surveyId={surveyId}
        treeKey={treeKey}
        treeConfig={treeConfig}
        isTreeConfigLoading={isTreeConfigLoading}
        followUpQuestions={followUpQuestionsFromMap}
        isTreeReady={isTreeReadyFromMap}
        isMale={isMale}
      />

      <div className="t_LiveTestingBodySelectTreeField flex-col gap-3">
        {/* 선택된 body part 그룹 목록 */}
        <div className="flex-col gap-3">
          {selectedKeyGroups?.map((keys, idx) => {
            if (isEmpty(keys)) return null;

            const notes = notesForKeyGroups[idx] || {};
            const partsStr = keys
              .map((k) => {
                const label = upperCase(getBodyPartLabel(k));
                const note = notes[k];
                return note ? `${label} (${truncate(note, { length: 30 })})` : label;
              })
              .join(', ');

            return (
              <div
                key={idx}
                className="p-1 border rounded-xl bg-primary bg-opacity-10 flex gap-2"
              >
                <span className="flex-1 p-3">
                  {partsStr}
                </span>
                <span className="flex-none flex gap-0 px-2">
                  <button
                    type="button"
                    className="btn btn-ghost px-2"
                    disabled={disabled}
                    onClick={() => handleEditClick(idx)}
                  >
                    <BiPencil size={20} />
                  </button>
                  <button
                    type="button"
                    className="btn btn-ghost px-2"
                    disabled={disabled}
                    onClick={() => handleDelete(idx)}
                  >
                    <BiTrash size={20} />
                  </button>
                </span>
              </div>
            );
          })}
        </div>

        {/* BODY PART 추가 버튼 */}
        <div className="flex-col">
          <button
            type="button"
            className="btn btn-outline btn-ghost btn-lg"
            disabled={disabled}
            onClick={handleAddClick}
          >
            <TbCirclePlus size={23} /> {sltStr({ key: 'btn-bodypart', upper: true })}
          </button>
        </div>
      </div>
    </>
  );
}

export default memo(LiveTestingBodySelectTreeField);
